import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useDeleteConnection } from '@/queries/connections/useDeleteConnection'
import { useStytchOrganization } from '@stytch/react/b2b'
import { Connection } from 'honeygrid-types/validators-and-types/ConnectionTypes'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { enqueueSnackbar } from 'notistack'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DeleteConfirmationDialog } from '../../DeleteConfirmationDialog'
import { ConnectionCardStatusRow } from './ConnectionCardStatusRow'

interface ConnectionCardProps {
	connection: Connection
}

export const ConnectionCard: FC<ConnectionCardProps> = ({ connection }) => {
	const { organization } = useStytchOrganization()
	const deleteConnectionMutation = useDeleteConnection()
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
	const navigate = useNavigate()

	const handleRemove = async () => {
		await deleteConnectionMutation.mutateAsync({
			workspaceId: organization?.organization_id,
			connectionId: connection.uuid,
		})
		enqueueSnackbar(`Removed ${connection.name}`, { variant: 'success' })
		setDeleteConfirmationOpen(false)
	}

	return (
		<>
			<Card
				className="transition-colors hover:bg-muted/50 cursor-pointer"
				onClick={() => navigate(connection.uuid)}
				data-testid="connection-card-container"
			>
				<div className="px-4 py-0 flex flex-col space-y-2">
					<div className="flex justify-between items-center">
						<h3 className="font-semibold leading-none tracking-tight truncate flex-1 mr-2">
							{connection.name}
						</h3>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									onClick={(e) => e.stopPropagation()}
									className="h-8 w-8 flex-shrink-0"
									data-testid="connection-card-menu-button"
								>
									<MoreHorizontal className="h-4 w-4" />
									<span className="sr-only">Open menu</span>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align="end"
								onClick={(e) => e.stopPropagation()}
								data-testid="connection-card-menu"
							>
								<DropdownMenuItem
									onClick={() => navigate(connection.uuid)}
									data-testid="connection-card-edit-button"
								>
									<Pencil className="mr-2 h-4 w-4" />
									<span>Edit</span>
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => {
										setDeleteConfirmationOpen(true)
									}}
									data-testid="connection-card-remove-button"
									variant="destructive"
								>
									<Trash2 className="mr-2 h-4 w-4" />
									<span>Remove</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
					<ConnectionCardStatusRow connection={connection} />
				</div>
			</Card>
			<DeleteConfirmationDialog
				isOpen={deleteConfirmationOpen}
				title={`${connection.name}`}
				message={`Are you sure you want to remove ${connection.name}? Campaigns will no longer be able to target using this connection.`}
				onRemove={handleRemove}
				onCancel={() => setDeleteConfirmationOpen(false)}
			/>
		</>
	)
}
